import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { RiCloseLine, RiUploadCloud2Line, RiFileTextLine } from 'react-icons/ri';
import GlassButton from '../ui/v2/GlassButton';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onUpload: (file: File, setAsDefault: boolean) => Promise<void>;
    isFirstResume?: boolean;
}

export default function UploadResumeModal({ isOpen, onClose, onUpload, isFirstResume = false }: Props) {
    const [file, setFile] = useState<File | null>(null);
    const [isDefault, setIsDefault] = useState(isFirstResume);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Force default if it's the first resume
    React.useEffect(() => {
        if (isOpen && isFirstResume) {
            setIsDefault(true);
        }
    }, [isOpen, isFirstResume]);

    if (!isOpen) return null;

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const selected = e.target.files[0];
            if (selected.size > 5 * 1024 * 1024) {
                setError("File size exceeds 5MB limit.");
                return;
            }
            setFile(selected);
            setError(null);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const selected = e.dataTransfer.files[0];
            if (selected.size > 5 * 1024 * 1024) {
                setError("File size exceeds 5MB limit.");
                return;
            }
            setFile(selected);
            setError(null);
        }
    };

    const handleSubmit = async () => {
        if (!file) return;
        setLoading(true);
        try {
            await onUpload(file, isDefault);
            onClose();
            // Reset
            setFile(null);
            setIsDefault(false);
        } catch (e) {
            setError("Failed to upload resume. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="w-full max-w-md bg-[#0B0B0F] border border-white/10 rounded-2xl shadow-2xl p-6 relative animate-in fade-in zoom-in-95 duration-200">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors"
                >
                    <RiCloseLine size={24} />
                </button>

                <h2 className="text-xl font-bold text-white mb-6">Upload Resume</h2>

                {!file ? (
                    <div
                        onClick={() => fileInputRef.current?.click()}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleDrop}
                        className="border-2 border-dashed border-white/10 rounded-xl p-8 text-center hover:border-blue-500/50 hover:bg-blue-500/5 transition-all cursor-pointer group"
                    >
                        <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-500/20 group-hover:text-blue-400 transition-colors text-zinc-500">
                            <RiUploadCloud2Line size={32} />
                        </div>
                        <p className="text-white font-medium mb-1">Click to upload or drag and drop</p>
                        <p className="text-sm text-zinc-500">PDF, DOC, DOCX — Max 5MB</p>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                            className="hidden"
                            onChange={handleFileChange}
                        />
                    </div>
                ) : (
                    <div className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-500/20 text-blue-400 rounded-lg flex items-center justify-center">
                                <RiFileTextLine size={20} />
                            </div>
                            <div className="overflow-hidden">
                                <p className="text-white text-sm font-medium truncate max-w-[200px]">{file.name}</p>
                                <p className="text-xs text-zinc-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                            </div>
                        </div>
                        <button
                            onClick={() => setFile(null)}
                            className="text-zinc-500 hover:text-red-400 transition-colors"
                        >
                            <RiCloseLine size={20} />
                        </button>
                    </div>
                )}

                {error && <p className="text-red-400 text-sm mt-3">{error}</p>}

                <div className="mt-6">
                    <div className="flex items-center gap-3">
                        <input
                            type="checkbox"
                            id="defaultResume"
                            checked={isDefault}
                            disabled={isFirstResume}
                            onChange={(e) => setIsDefault(e.target.checked)}
                            className={`w-4 h-4 rounded border-white/20 bg-white/5 text-blue-500 focus:ring-blue-500/50 focus:ring-offset-0 ${isFirstResume ? 'opacity-50 cursor-not-allowed' : ''}`}
                        />
                        <label htmlFor="defaultResume" className={`text-sm text-zinc-400 select-none ${isFirstResume ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                            Set as default resume
                        </label>
                    </div>
                    {isFirstResume && (
                        <p className="text-xs text-blue-400 mt-2 ml-7">
                            Your first resume is automatically set as your default.
                        </p>
                    )}
                </div>

                <div className="mt-6 flex justify-end gap-3">
                    <GlassButton variant="secondary" onClick={onClose} disabled={loading}>
                        Cancel
                    </GlassButton>
                    <GlassButton
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={!file || loading}
                        className={loading ? 'opacity-70 cursor-not-allowed' : ''}
                    >
                        {loading ? 'Uploading...' : 'Upload Resume'}
                    </GlassButton>
                </div>
            </div>
        </div>,
        document.body
    );
}
